import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { cpeService } from '../services/cpeService';
import { CPE } from '../types/cpe';
import { useAuth } from '../contexts/AuthContext';

interface CPEDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  cpeId: string | null;
  onEdit?: (cpeId: string) => void;
  onDelete?: (cpeId: string) => void;
  onRestore?: (cpeId: string) => void;
}

export const CPEDetailsDialog: React.FC<CPEDetailsDialogProps> = ({
  open,
  onClose,
  cpeId,
  onEdit,
  onDelete,
  onRestore,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cpe, setCPE] = useState<CPE | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canDelete = user?.role === 'super_admin' || (user?.role === 'admin' && user?.can_delete_cpe);

  const loadCPE = useCallback(async () => {
    if (!cpeId) return;

    try {
      setLoading(true);
      setError('');
      const data = await cpeService.getCPEById(cpeId);
      setCPE(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to load CPE details');
    } finally {
      setLoading(false);
    }
  }, [cpeId]);

  useEffect(() => {
    if (open && cpeId) {
      loadCPE();
    }
  }, [open, cpeId, loadCPE]);

  const handleEdit = () => {
    if (cpeId && onEdit) {
      onEdit(cpeId);
    } else if (cpeId) {
      navigate(`/cpes/${cpeId}/edit`);
    }
    onClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cpeId) return;

    try {
      setDeleting(true);
      await cpeService.deleteCPE(cpeId);
      setDeleteDialogOpen(false);
      if (onDelete) {
        onDelete(cpeId);
      }
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to delete CPE');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = () => {
    if (cpeId && onRestore) {
      onRestore(cpeId);
    }
    onClose();
  };

  const handleOpenInNewPage = () => {
    if (cpeId) {
      navigate(`/cpes/${cpeId}`);
    }
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{cpe?.name || t('cpe.cpeDetails')}</Typography>
        <Box>
          <IconButton onClick={handleOpenInNewPage} title={t('common.viewDetails')}>
            <LaunchIcon />
          </IconButton>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : cpe ? (
          <Grid container spacing={3}>
            {/* Image */}
            {cpe.image_url && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={cpeService.getImageUrl(cpe.image_url) || ''}
                    alt={cpe.name}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 300,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* Status Chips */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {cpe.deleted_at && (
                  <Chip label={t('users.deleted')} color="error" size="small" />
                )}
                {cpe.plant_type.map((type) => (
                  <Chip key={type} label={type} color="primary" size="small" />
                ))}
                {cpe.cpe_status && (
                  <Chip label={cpe.cpe_status} color="info" size="small" />
                )}
                {cpe.internal_positioning && (
                  <Chip
                    label={cpe.internal_positioning === 'go' ? t('cpe.positioning.go') : t('cpe.positioning.noGo')}
                    color={cpe.internal_positioning === 'go' ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </Box>
            </Grid>

            {/* Details Table */}
            <Grid item xs={12}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold', width: '40%' }}>
                      {t('cpe.fields.location')}
                    </TableCell>
                    <TableCell>
                      {/* Coordinates until the location has been reverse-geocoded */}
                      {[cpe.locality, cpe.county].filter(Boolean).join(', ') || `${cpe.latitude}, ${cpe.longitude}`}
                      {(cpe.locality || cpe.county) && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {cpe.latitude}, {cpe.longitude}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>

                  {cpe.power && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.power')}
                      </TableCell>
                      <TableCell>{cpe.power} MW</TableCell>
                    </TableRow>
                  )}

                  {cpe.energization_year && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.energizationYear')}
                      </TableCell>
                      <TableCell>{cpe.energization_year}</TableCell>
                    </TableRow>
                  )}

                  {cpe.commercial_status && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.commercialStatus')}
                      </TableCell>
                      <TableCell>{cpe.commercial_status}</TableCell>
                    </TableRow>
                  )}

                  {cpe.company_cui && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.companyCui')}
                      </TableCell>
                      <TableCell>{cpe.company_cui}</TableCell>
                    </TableRow>
                  )}

                  {cpe.epc && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.epc')}
                      </TableCell>
                      <TableCell>{cpe.epc}</TableCell>
                    </TableRow>
                  )}

                  {cpe.dispatcher && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.dispatcher')}
                      </TableCell>
                      <TableCell>{cpe.dispatcher}</TableCell>
                    </TableRow>
                  )}

                  {cpe.maintenance_company && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.maintenanceCompany')}
                      </TableCell>
                      <TableCell>{cpe.maintenance_company}</TableCell>
                    </TableRow>
                  )}

                  {cpe.om_account_manager && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.omAccountManager')}
                      </TableCell>
                      <TableCell>{cpe.accountManager?.name || cpe.om_account_manager}</TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      {t('cpe.fields.exactLocation', 'Exact Location')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cpe.exact_location ? t('common.yes') : t('common.no')}
                        color={cpe.exact_location ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                      {t('cpe.fields.isWaldevarPP', 'Waldevar PP')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cpe.is_waldevar_pp ? t('common.yes') : t('common.no')}
                        color={cpe.is_waldevar_pp ? 'primary' : 'default'}
                        size="small"
                        sx={cpe.is_waldevar_pp ? { bgcolor: '#1d388d', color: '#fff' } : {}}
                      />
                    </TableCell>
                  </TableRow>

                  {cpe.internal_link && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.internalLink', 'Internal Link')}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          component="a"
                          href={cpe.internal_link.startsWith('http') ? cpe.internal_link : `https://${cpe.internal_link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<LaunchIcon />}
                        >
                          {t('common.openProject')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}

                  {cpe.project_link && (
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {t('cpe.fields.externalLink', 'External Link')}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          component="a"
                          href={cpe.project_link.startsWith('http') ? cpe.project_link : `https://${cpe.project_link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<LaunchIcon />}
                        >
                          {t('common.openLink', 'Open Link')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Grid>

            {/* Description and Notes */}
            {cpe.short_description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('cpe.fields.shortDescription')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cpe.short_description}
                </Typography>
              </Grid>
            )}

            {cpe.notes && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('cpe.fields.notes')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cpe.notes}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Metadata */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('cpe.fields.createdBy')}: {cpe.creator?.name || cpe.creator?.email || 'Unknown'} on{' '}
                {new Date(cpe.created_at).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('cpe.fields.lastUpdatedBy')}: {cpe.updater?.name || cpe.updater?.email || 'Unknown'} on{' '}
                {new Date(cpe.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />

        {cpe?.deleted_at && user?.role === 'super_admin' && (
          <Button
            variant="contained"
            color="success"
            startIcon={<RestoreIcon />}
            onClick={handleRestore}
          >
            {t('cpe.restoreCpe')}
          </Button>
        )}

        {!cpe?.deleted_at && (
          <>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                {t('common.edit')}
              </Button>
            )}

            {canDelete && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                {t('common.delete')}
              </Button>
            )}
          </>
        )}

        <Button variant="outlined" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('common.deleteConfirmMessage')} <strong>{cpe?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action can be reversed by a Super Admin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
